<template>
  <PortalLayout section="Users">
    <div class="mt-4">
      <div v-if="isPeopleLoading"><Loading /></div>
      <div v-else>
        <div class="lg:flex mb-4">
          <div class="lg:w-1/3 sm:w-2/3 mb-2 lg:mb-0">
            <div class="pb-2">
              <BaseCheckbox v-model="hideDisabledUsers" label="Hide Inactive Users" />
            </div>

            <BaseSelect
              class="text-sm"
              :options="sortTypeOptions"
              :defaultOption="defaultTypeOption"
              v-model="selectedSortType"
            />
          </div>
          <span class="flex-grow" />
          <span class="w-full sm:w-2/3 lg:w-1/3 mt-auto px-3">
            <input
              v-model="filterString"
              placeholder="Filter Name"
              class="h-min shadow appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline w-full"
            />
            <span>
              <BaseSelect
                class="text-sm"
                :options="permissionOptions"
                :defaultOption="null"
                defaultText="Permissions Filter"
                v-model="selectedPermissionFilter" />
              <BaseSelect
                class="text-sm"
                :options="roleOptions"
                :defaultOption="null"
                defaultText="Role Filter"
                v-model="selectedRoleFilter"
            /></span>
          </span>
        </div>
        <AddButton @click="openAddPopup">Add User</AddButton>
        <div v-if="filteredPeople?.length ?? 0 > 0">
          <div
            v-for="person in filteredPeople"
            :key="person.personId"
            @click="viewPersonClick(person)"
            class="px-4 py-2 my-2 border-t cursor-pointer hover:bg-blue-200"
            :class="person.dateDisabled ? 'bg-gray-300' : 'bg-gray-100'"
          >
            <div>
              {{ person.firstName }} {{ person.lastName }}
              <span class="ml-2 text-sm text-gray-700" :class="person.dateDisabled ? '' : 'mr-2'">
                {{ person.loginEmail }}
              </span>
              <span v-if="person.dateDisabled" class="ml-2 text-gray-500 mr-2"> (Disabled) </span>
              <span v-if="person.isStaff" class="has-tooltip inline-block align-bottom ml-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="size-6"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Zm6-10.125a1.875 1.875 0 1 1-3.75 0 1.875 1.875 0 0 1 3.75 0Zm1.294 6.336a6.721 6.721 0 0 1-3.17.789 6.721 6.721 0 0 1-3.168-.789 3.376 3.376 0 0 1 6.338 0Z"
                  />
                </svg>
                <span class="tooltip text-xs shadow-lg p-1 bg-gray-200 text-gray-700 -mt-12">
                  Staff
                </span>
              </span>
              <span
                v-if="person.permissions.filter((p) => p.permissionType != null).length > 0"
                class="has-tooltip inline-block align-bottom ml-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="size-6"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
                  />
                </svg>
                <span class="tooltip text-xs shadow-lg p-1 bg-gray-200 text-gray-700 -mt-12">
                  Has Admin Permissions
                </span>
              </span>
            </div>
          </div>
        </div>
        <div v-else-if="people?.length ?? 0 > 0" class="mt-2">
          <span>-- No Users found with current filter --</span>
        </div>
        <div v-else>
          <span>-- No Users found for this building --</span>
        </div>
        <BaseButton class="mt-4" v-if="people?.length > 0" @click="removeTestUsersClick">
          Remove Test Users
        </BaseButton>
      </div>
    </div>
    <EditPopup
      entityName="User"
      :saveError="saveError"
      :isVisible="isAddPopupVisible"
      saveButtonText="Add User"
      @save="saveAddUser"
      @close="isAddPopupVisible = false"
    >
      <div class="sm:flex">
        <div class="basis-1/2 sm:pr-2">
          <BaseInput
            v-model="personLoginEmail"
            :error="errors.personLoginEmail"
            label="Login Email"
          />
        </div>
        <div class="basis-1/2 sm:pl-2">
          <BaseInput v-model="personPassword" :error="errors.personPassword" label="Password" />
        </div>
      </div>
      <div class="sm:flex">
        <div class="basis-1/2 sm:pr-2">
          <BaseInput
            v-model="personFirstName"
            :error="errors.personFirstName"
            label="First Name"
            type="text"
          />
        </div>
        <div class="basis-1/2 sm:pl-2">
          <BaseInput
            v-model="personLastName"
            :error="errors.personLastName"
            label="Last Name"
            type="text"
          />
        </div>
      </div>
      <BaseSelect
        label="Tenant"
        v-model="personTenantId"
        :error="errors.personTenantId"
        :options="tenants"
        :default-option="({ name: '--Please Select--', value: 0 } as NameValue)"
      />
      <div class="xl:flex">
        <div class="basis-1/2 xl:pr-2">
          <BaseSelect
            label="Primary Floor"
            v-model="personPrimaryFloor"
            :error="errors.personPrimaryFloor"
            :options="property?.floorList"
          />
        </div>
        <div class="basis-1/2 xl:pl-2">
          <BaseSelect
            label="Building Role"
            v-model="personSiteRoleId"
            :error="errors.personSiteRoleId"
            :options="siteRoles"
            :default-option="({ name: '--Please Select--', value: 0 } as NameValue)"
          />
          <div class="mt-2" />
        </div>
      </div>
    </EditPopup>
    <BasePopup
      class="w-1/5"
      :isVisible="isPopupVisible"
      title="This will remove all test user registrations for this portal. Please confirm."
      @close="isPopupVisible = false"
    >
      <div class="mb-4">
        <Loading v-if="isPopupLoading" />
        <div
          v-if="popupMessage"
          class="ft-mono py-4 px-8 rounded"
          :class="popupSuccessful ? 'bg-green-100' : 'bg-red-100'"
        >
          {{ popupMessage }}
        </div>
      </div>
      <BaseButton @click="isPopupVisible = false">
        {{ popupSuccessful ? "Close" : "Cancel" }}
      </BaseButton>
      <BaseButton
        v-show="!popupMessage"
        class="bg-blue-300 float-right"
        :class="{ invisible: popupSuccessful }"
        @click="confirmRemoveTestUsers"
      >
        Confirm
      </BaseButton>
    </BasePopup>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { usePersonStore } from "../../store/personStore";
import { PermissionType, PersonSearchDTO, RegisterRequest } from "../../store/generatedFetchClient";
import { addToRecentlyUsedUsers } from "../../helpers/index";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useUserStore } from "../../store/userStore";
import { NameValue } from "../../sharedTypes";

const bldgStore = useBldgStore();
const userStore = useUserStore();
const personStore = usePersonStore();
const router = useRouter();
let hideDisabledUsers = ref(true);
enum SortType {
  FirstName = 1,
  LastName,
  LoginEmail,
}

let sortTypeOptions = ref([
  { name: "Sort by Last Name", value: SortType.LastName },
  { name: "Sort by Login Email", value: SortType.LoginEmail },
] as NameValue[]);
let defaultTypeOption = ref({ name: "Sort by First Name", value: SortType.FirstName });

let isPeopleLoading = ref(false);
var property = computed(() => bldgStore.property);

let people = computed(() => {
  let peopleList = personStore.people;
  if (hideDisabledUsers.value) {
    return peopleList.filter((p) => !p.dateDisabled);
  }
  return peopleList;
});
let filteredPeople = computed(() => {
  let filterStringLower = filterString.value.toLowerCase();
  let filtered = people.value
    .filter(
      (f) =>
        f.firstName.toLowerCase().startsWith(filterStringLower) ||
        f.lastName.toLowerCase().startsWith(filterStringLower) ||
        f.loginEmail.toLowerCase().startsWith(filterStringLower)
    )
    .sort((a, b) => {
      if (selectedSortType.value == SortType.FirstName)
        return a.firstName.localeCompare(b.firstName);
      else if (selectedSortType.value == SortType.LastName)
        return a.lastName.localeCompare(b.lastName);
      else if (selectedSortType.value == SortType.LoginEmail)
        return a.loginEmail.localeCompare(b.loginEmail);
    });

  if (selectedPermissionFilter.value != null && selectedPermissionFilter.value.toString() != "") {
    filtered = filtered.filter((f) =>
      f.permissions
        .filter((p) => p.permissionType != null)
        .map((p) => p.permissionType)
        .some((p) => p == selectedPermissionFilter.value)
    );
  }

  if (selectedRoleFilter.value != null && selectedRoleFilter.value.toString() != "") {
    filtered = filtered.filter((f) => f.siteRoleId == selectedRoleFilter.value);
  }

  return filtered;
});
let filterString = ref("");
let selectedSortType = ref(SortType.FirstName);

onMounted(async () => await loadPeople());
watch(property, loadPeople);

async function loadPeople() {
  console.log("Users page loading data");
  isPeopleLoading.value = true;
  if (property?.value?.portalId) {
    await personStore.getForPortal(property?.value?.portalId);
  }
  isPeopleLoading.value = false;
}

function viewPersonClick(person: PersonSearchDTO) {
  addToRecentlyUsedUsers({ siteCode: property.value.siteCode, ...person });
  router.push(`user/${person.personId}`);
}

// #region Add user
const isAddPopupVisible = ref(false);
const saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    personLoginEmail: yup.string().required().email().max(255).label("Login Email"),
    personPassword: yup.string().required().min(5).max(255).label("Password"),
    personFirstName: yup.string().required().max(50).label("First Name"),
    personLastName: yup.string().notRequired().max(50).label("Last Name"),
    personTenantId: yup
      .number()
      .required()
      .test(
        "valid-tenant-vr",
        (d) => `Tenant is required for VR-enabled portals`,
        (value) => (property.value?.isVrtourEnabled ? value > 0 : true)
      )
      .label("Tenant"),
    personPrimaryFloor: yup.string().nullable().notRequired().max(50).label("Primary Floor"),
    personSiteRoleId: yup
      .number()
      .min(1, "Building Role is a required field")
      .required()
      .label("Building Role"),
  }),
});

const { value: personLoginEmail } = useField<string>("personLoginEmail");
const { value: personPassword } = useField<string>("personPassword");
const { value: personFirstName } = useField<string>("personFirstName");
const { value: personLastName } = useField<string>("personLastName");
const { value: personTenantId } = useField<number>("personTenantId");
const { value: personPrimaryFloor } = useField<string | null>("personPrimaryFloor");
const { value: personSiteRoleId } = useField<number>("personSiteRoleId");
let siteRoles = computed(() =>
  property.value?.siteRoles?.map(
    (r) =>
      ({
        name: r.roleAlias + (r.isStaffRole ? " (Staff)" : ""),
        value: r.siteRoleId,
      } as NameValue)
  )
);
let tenants = computed(() =>
  property.value?.tenants?.map((t) => ({ name: t.name, value: t.tenantId } as NameValue))
);

function openAddPopup() {
  personLoginEmail.value = "";
  personPassword.value = "";
  personFirstName.value = "";
  personLastName.value = "";
  personTenantId.value = 0;
  personPrimaryFloor.value = null;
  personSiteRoleId.value = 0;

  isAddPopupVisible.value = true;
}

const saveAddUser = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let savePerson = {
    portalId: property.value?.portalId,
    loginEmail: personLoginEmail.value,
    password: personPassword.value,
    firstName: personFirstName.value,
    lastName: personLastName.value,
    primaryFloor: personPrimaryFloor.value,
    siteRoleId: personSiteRoleId.value,
    tenantId: personTenantId.value > 0 ? personTenantId.value : null,
  } as RegisterRequest;

  saveError.value = "";
  saveError.value = await userStore.createAccount(savePerson);
  if (saveError.value) return;

  isAddPopupVisible.value = false;
  await personStore.getForPortal(property?.value?.portalId, true);
}

function onInvalidSubmit() {
  saveError.value = "One or more invalid form inputs.";
}

// #endregion Add user

// #region Remove test users
let isPopupVisible = ref(false);
let isPopupLoading = ref(false);
let popupMessage = ref("");
let popupSuccessful = ref(false);

function removeTestUsersClick() {
  popupMessage.value = "";
  popupSuccessful.value = false;

  isPopupVisible.value = true;
}

async function confirmRemoveTestUsers() {
  isPopupLoading.value = true;
  popupMessage.value = await personStore.deletePortalTestPeople(property.value.portalId);

  if (!popupMessage.value) {
    popupMessage.value = "Successfully disabled test users.";
    popupSuccessful.value = true;
  } else {
    popupSuccessful.value = false;
  }

  await personStore.getForPortal(property?.value?.portalId, true);
  isPopupLoading.value = false;
}
// #endregion Remove test users

// #region filter options
let permissionOptions = [
  { name: "Can View Training Report", value: PermissionType.ViewTrainingReport },
  { name: "Can View Portal Roster", value: PermissionType.ViewPortalRoster },
  { name: "Can View Mobility Report", value: PermissionType.ViewMobilityReport },
  { name: "Can View VR Report", value: PermissionType.ViewVRReport },
  { name: "Can View VR Filter Report", value: PermissionType.ViewVRFilterReport },
  {
    name: "Can View Tenant Contact Report",
    value: PermissionType.ViewTenantContactReport,
  },
  { name: "Can View Users", value: PermissionType.ViewUsers },
  { name: "Can Edit Users", value: PermissionType.EditUsers },
  { name: "Can Edit Documents", value: PermissionType.EditDocuments },
  { name: "Can Edit Contacts", value: PermissionType.EditContacts },
  { name: "Can View Alert Subscriptions", value: PermissionType.ViewAlertSubscriptions },
  { name: "Can Send Alerts", value: PermissionType.SendAlerts },
  { name: "Can Edit Respond Content", value: PermissionType.EditRespond },
  { name: "Can Edit Tenants", value: PermissionType.EditTenant },
];

let selectedPermissionFilter: Ref<PermissionType | null> = ref(null);

let roleOptions = computed(() =>
  property.value.siteRoles.map((r) => {
    return { name: r.roleAlias, value: r.siteRoleId };
  })
);

let selectedRoleFilter: Ref<number | null> = ref(null);
// #endregion filter options
</script>
